import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, FileText, Video, Home, Plus, Activity } from 'lucide-react';
import { PageTitle } from '../components/PageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalProperties: 0,
    totalPosts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadStats();
    // Actualizar cada 10 segundos
    const interval = setInterval(loadStats, 10000);
    return () => clearInterval(interval);
  }, [user]);

  const loadStats = async () => {
    try {
      const [usersRes, propsRes, postsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/properties`),
        axios.get(`${API}/community/posts`)
      ]);
      
      const usersList = usersRes.data.users || usersRes.data || [];
      const activeUsersCount = usersList.filter(u =>
        ['profesional', 'premium', 'usuario_avanzado', 'essential'].includes(u.subscription_plan) && !u.banned
      ).length;
      
      setStats({
        totalUsers: usersList.length,
        activeUsers: activeUsersCount,
        totalProperties: propsRes.data.length || 0,
        totalPosts: postsRes.data.length || 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Error al cargar estadísticas');
      setLoading(false);
    }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-gray-950 p-6">
      <div className="max-w-7xl mx-auto pt-8">
        <PageTitle>Dashboard Administrativo</PageTitle>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<Users className="text-blue-500" size={32} />}
            title="Usuarios Totales"
            value={stats.totalUsers}
            subtitle="Registrados"
          />
          <StatCard
            icon={<Activity className="text-green-500" size={32} />}
            title="Usuarios Activos"
            value={stats.activeUsers}
            subtitle="Con suscripción activa"
          />
          <StatCard
            icon={<Home className="text-yellow-500" size={32} />}
            title="Propiedades"
            value={stats.totalProperties}
            subtitle="Publicadas"
          />
          <StatCard
            icon={<FileText className="text-purple-500" size={32} />}
            title="Posts Comunidad"
            value={stats.totalPosts}
            subtitle="Totales"
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-gray-900 border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="text-2xl font-bold text-white mb-6">Acciones Rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ActionButton
              icon={<Home />}
              label="Nueva Propiedad"
              onClick={() => navigate('/publish')}
              color="bg-yellow-500"
            />
            <ActionButton
              icon={<FileText />}
              label="Nuevo Blog"
              onClick={() => navigate('/knowledge')}
              color="bg-purple-500"
            />
            <ActionButton
              icon={<Users />}
              label="Gestionar Usuarios"
              onClick={() => navigate('/admin/users')}
              color="bg-blue-500"
            />
            <ActionButton
              icon={<Video />}
              label="Añadir Vídeo"
              onClick={() => navigate('/admin/videos')}
              color="bg-red-500"
            />
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-gray-900 border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="text-2xl font-bold text-white mb-4">Actividad Reciente</h2>
          <p className="text-gray-400 text-sm">Actualización en tiempo real cada 10 segundos</p>
          <div className="mt-4 text-gray-500 text-sm">
            {loading ? 'Cargando...' : `Última actualización: ${new Date().toLocaleTimeString()}`}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, subtitle }) => (
  <div className="bg-gray-900 border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition">
    <div className="flex items-center justify-between mb-4">
      {icon}
      <span className="text-4xl font-black text-white">{value}</span>
    </div>
    <h3 className="text-lg font-bold text-white">{title}</h3>
    <p className="text-sm text-gray-400">{subtitle}</p>
  </div>
);

const ActionButton = ({ icon, label, onClick, color }) => (
  <button
    onClick={onClick}
    className={`${color} text-white px-6 py-4 font-bold uppercase flex items-center justify-center gap-3 border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition`}
  >
    {icon}
    <span className="text-sm">{label}</span>
  </button>
);
